import { expressionVariablesConstants } from '@shared/constants/expression-variables.constants';
import { CALCULATIONS } from '@shared/constants/calculations.constants';
import { roundToDecimals } from '@shared/helpers/math.helpers';
import { selectByIndexesOrNames } from '@shared/helpers/array.helpers';
import {
  emptyStoreySummedValues,
  IEnrichedStorey,
  IStoreySum,
  StoreySelector,
} from '@shared/helpers/storeys.helpers';
import {
  DesignExpressionVariables,
  StoreyExpressionVariables,
  StoreySummaryVariables,
  emptyStoreySummaryVariables,
} from '@shared/models/design/design-expression-variables.model';
import { ActivityId } from '@shared/models/activity-class.model';
import { getActivityDerivedStoreyValues } from './activity-expression-variables.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { getKeys, omit } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  activityExpressionVariablesRecordToActivityMix,
  getLargestProgramActivityTypeFromActivityMix,
} from '../activity-mix.helpers';

type RectangleDimensions = { width: number; length: number };

type SumStoreysFromDesignOptions = {
  activityId?: ActivityId;
  selectors?: StoreySelector[];
  /**
   * Site GFA used as the activity-share denominator. Defaults to this stack's
   * GFA. Pass the sum of every building when combining stacks so garage and
   * other program areas are allocated once, not once per building.
   */
  siteGfa?: number;
};

/**
 * Gross floor area of one building's storey stack.
 */
export const getStackGfa = (storeys: StoreyExpressionVariables[]): number =>
  storeys.reduce((acc, storey) => acc + storey.gfa, 0);

/**
 * Floor count for `storeys_count`: the tallest stack, not the number of
 * storey objects on the site. `getStorey(storeys_count)` is then the last
 * floor of every building that has that many floors.
 */
export const getStoreysCountFromStacks = (
  stacks: ReadonlyArray<readonly StoreyExpressionVariables[]>,
): number => Math.max(0, ...stacks.map((storeys) => storeys.length), 0);

/** Stack heights are per-building totals; combining buildings must not add them. */
const BUILDING_HEIGHT_KEYS_TO_MAX = [
  'total_height',
  'total_height_above_ground',
  'total_height_below_ground',
  'inner_height',
] as const satisfies ReadonlyArray<keyof IStoreySum>;

const BUILDING_HEIGHT_KEYS_TO_MAX_SET = new Set<keyof IStoreySum>(
  BUILDING_HEIGHT_KEYS_TO_MAX,
);

/**
 * Combine one {@link IStoreySum} per building. Perimeter, areas, counts, GWA,
 * and volume are summed. Height fields stay the tallest stack so two volumes
 * are not stacked vertically. Elevation stays the deepest.
 */
const combineBuildingStoreySums = (storeys: IStoreySum[]): IStoreySum => {
  if (storeys.length === 0) return { ...emptyStoreySummedValues };
  const sum: IStoreySum = {
    ...emptyStoreySummedValues,
    elevation: storeys[0]?.elevation ?? 0,
  };
  const sumKeys = getKeys(omit(sum, 'name', 'elevation'));
  storeys.forEach((storey) => {
    sumKeys.forEach((key) => {
      const value = storey[key];
      if (!guards.is.number(value)) return;
      if (BUILDING_HEIGHT_KEYS_TO_MAX_SET.has(key)) {
        sum[key] = Math.max(sum[key], value);
        return;
      }
      sum[key] += value;
    });
    sum.elevation = Math.min(sum.elevation, storey.elevation);
  });
  sum.name = storeys
    .map((storey, index) => storey.name || `Storey ${index}`)
    .join(', ');
  return sum;
};

/**
 * Combine per-building {@link StoreySummaryVariables}: sum GFA/GWA/perimeter,
 * take the tallest stack.
 */
export const combineStoreySummaryVariables = (
  summaries: StoreySummaryVariables[],
): StoreySummaryVariables => {
  const [first, ...rest] = summaries;
  if (!first) return { ...emptyStoreySummaryVariables };
  return rest.reduce((acc, summary) => {
    const merged = { ...emptyStoreySummaryVariables };
    getKeys(emptyStoreySummaryVariables).forEach((key) => {
      merged[key] =
        key === 'inner_height' || key === 'total_height'
          ? Math.max(acc[key], summary[key])
          : acc[key] + summary[key];
    });
    return merged;
  }, first);
};

/**
 * Overlay party-wall {@link StoreySummaryVariables.gwa} from storey stacks.
 * {@link CALCULATIONS} GWA is perimeter × height and over-subtracts when a
 * neighbour is shorter.
 */
export const withPartyWallGwa = (
  summary: StoreySummaryVariables,
  storeys: StoreyExpressionVariables[],
): StoreySummaryVariables => ({
  ...summary,
  gwa: storeys.reduce((sum, storey) => sum + storey.gwa, 0),
});

/**
 * Estimate rectangle width/length from area and perimeter.
 * Migrated from legacy `getStoreyBuildingValues`; used by storey sums and design→project overlay.
 */
export const estimateRectangleDimensions = (input: {
  area: number;
  perimeter: number;
}): RectangleDimensions => {
  const { area, perimeter } = input;
  if (area <= 0 || perimeter <= 0) return { width: 0, length: 0 };

  const s = perimeter / 2;
  const discriminant = s * s - 4 * area;
  if (discriminant <= 0) {
    const side = s / 2;
    return { width: side, length: side };
  }

  const width = (s - Math.sqrt(discriminant)) / 2;
  if (!guards.is.finiteNumber(width) || width <= 0) {
    return { width: 0, length: 0 };
  }

  return { width, length: area / width };
};

/**
 * Prefer geometric cover from any touching storey (same building or a parent
 * sitting on this roof). Fall back to the same-building stack GFA when cover
 * is 0 so L-shapes without overlap still use the legacy setback/cantilever
 * approximation instead of looking like a free slab or missing sky.
 */
const coveringAreaOrStack = (
  covered: number | undefined,
  stackArea: number,
): number => {
  const area = covered ?? 0;
  return area > 0 ? area : stackArea;
};

/**
 * Like legacy {@link getStoreyBuildingValues} but for design storey expression variables.
 * Mezzanine storeys cannot provide envelope values, so we fallback to the latest non-mezzanine storey below.
 */
const getDesignStoreyBuildingValues = (
  storeys: StoreyExpressionVariables[],
  floorIndex: number,
): { storeyArea: number; storeyPerimeter: number } => {
  const [firstStorey] = storeys;
  if (!firstStorey) return { storeyArea: 0, storeyPerimeter: 0 };
  if (floorIndex < 0 || floorIndex >= storeys.length) {
    return { storeyArea: 0, storeyPerimeter: 0 };
  }

  for (let i = floorIndex; i >= 0; i--) {
    const storey = storeys[i];
    if (!storey) continue; // should never happen
    if (storey.mezzanine) continue;
    return {
      storeyArea: storey.gfa,
      storeyPerimeter: storey.perimeter,
    };
  }

  // We should only get here if the first storey is mezzanine; ignore mezzanine-flag in that case.
  return {
    storeyArea: firstStorey.gfa,
    storeyPerimeter: firstStorey.perimeter,
  };
};

/**
 * Calculate gfa available for activities on a storey. Ported 1:1 from `results.helpers.ts`.
 */
const getWidthOfMinimumRoomHeight = (
  buildingWidth: number,
  innerHeight: number,
  roofPitch: number,
): number => {
  const minHeight = 1.9 - innerHeight;
  if (!roofPitch || minHeight <= 0) return buildingWidth;

  // +60cm after the point where height is 1.9m height is counted as width
  const minWidth =
    0.6 * 2 +
    buildingWidth -
    (2 * minHeight) / Math.tan((roofPitch * Math.PI) / 180);

  return Math.min(buildingWidth, minWidth);
};

const getActivitiesGFA = (
  gfaSky: number,
  gfa: number,
  buildingWidth: number,
  innerHeight: number,
  roofPitch = 0,
): number => {
  const length = buildingWidth ? gfa / buildingWidth : 0;
  const lengthWithRoof = gfa ? length * (gfaSky / gfa) : 0;
  const lengthWithoutRoof = length - lengthWithRoof;
  const areaWithoutRoof = lengthWithoutRoof * buildingWidth;
  const areaWithRoof =
    lengthWithRoof *
    getWidthOfMinimumRoomHeight(buildingWidth, innerHeight, roofPitch);

  return roundToDecimals(areaWithoutRoof + areaWithRoof);
};

export const getInnerHeightFromDesignHeight = (totalHeight: number): number => {
  const slab = expressionVariablesConstants.floor_slab_height;
  return Math.max(0, totalHeight - slab);
};

/**
 * @deprecated TODO: Rewrite
 * Convert a single design storey into a fully calculated legacy {@link IStoreySum}.
 * TODO: Go through and remove obsolete properties!!
 * This is intentionally storey-scoped (no activity references). If you want lowest-level calculation,
 * compute this once and then distribute "amount-like" variables to activity references by GFA share.
 */
export const calculateStoreySumFromDesignStoreyVariables = (
  designVariables: Pick<DesignExpressionVariables, 'storeys' | 'programs'>,
  floorIndex: number,
  options: {
    activityId?: ActivityId;
    name?: string;
    siteGfa?: number;
  } = {},
): IStoreySum => {
  const storeys = designVariables.storeys;
  const storey = storeys[floorIndex];
  if (!storey) return { ...emptyStoreySummedValues };

  const name = options.name ?? `Storey ${floorIndex}`;
  const activityId = options.activityId ?? ActivityId.PrivateHousing;

  const stackGfa = getStackGfa(storeys);
  const sumGFA = options.siteGfa ?? stackGfa;
  const share = sumGFA > 0 ? storey.gfa / sumGFA : 0;

  const { storeyArea, storeyPerimeter } = getDesignStoreyBuildingValues(
    storeys,
    floorIndex,
  );

  // Mezzanine storeys do not contribute to building height (align with designVariables.height / building_height).
  const total_height = storey.mezzanine ? 0 : storey.height;
  const inner_height = getInnerHeightFromDesignHeight(total_height);
  const { elevation, height } = storey;
  const gfa = storey.gfa;

  const total_height_above_ground =
    CALCULATIONS.total_height_above_ground.calculate({
      elevation,
      total_height,
    });
  const total_height_below_ground =
    CALCULATIONS.total_height_below_ground.calculate({
      total_height,
      total_height_above_ground,
    });

  // Use actual storey height only for GFA above/below classification so mezzanines in basements get gfa_below_ground.

  const total_height_below_ground_for_gfa =
    height -
    CALCULATIONS.total_height_above_ground.calculate({
      elevation,
      total_height: height,
    });
  const gfa_above_ground = CALCULATIONS.gfa_above_ground.calculate({
    total_height_below_ground: total_height_below_ground_for_gfa,
    gfa,
  });
  const gfa_below_ground = CALCULATIONS.gfa_below_ground.calculate({
    total_height_below_ground: total_height_below_ground_for_gfa,
    gfa,
  });

  const external_gwa = CALCULATIONS.external_gwa.calculate({
    perimeter: storeyPerimeter,
    total_height,
  });
  const external_gwa_above_ground =
    CALCULATIONS.external_gwa_above_ground.calculate({
      perimeter: storeyPerimeter,
      total_height_above_ground,
    });
  const external_gwa_below_ground =
    CALCULATIONS.external_gwa_below_ground.calculate({
      perimeter: storeyPerimeter,
      total_height_below_ground,
    });

  const gross_volume = CALCULATIONS.gross_volume.calculate({
    gfa: storeyArea,
    total_height,
  });
  const gross_volume_above_ground =
    CALCULATIONS.gross_volume_above_ground.calculate({
      gfa: storeyArea,
      total_height_above_ground,
    });
  const gross_volume_below_ground =
    CALCULATIONS.gross_volume_below_ground.calculate({
      gfa: storeyArea,
      total_height_below_ground,
    });

  let gfa_ground_floor_slabs = 0;
  let gfa_cantilevered_floor_slabs = 0;
  let gfa_terrace_slabs = 0;
  let gfa_sky = 0;

  // Storey area deltas ignore mezzanine storeys (same semantics as legacy getStoreyBuildingValues)
  const { storeyArea: prevFromStack } = getDesignStoreyBuildingValues(
    storeys,
    floorIndex - 1,
  );
  const { storeyArea: nextFromStack } = getDesignStoreyBuildingValues(
    storeys,
    floorIndex + 1,
  );
  // covered_gfa_below/above include a covering parent from another building.
  // 0 means "use the same-building stack" so L-shape / failed overlap does
  // not look like a free cantilever or a fully exposed roof.
  const prevStoreyArea = coveringAreaOrStack(
    storey.covered_gfa_below,
    prevFromStack,
  );
  const nextStoreyArea = coveringAreaOrStack(
    storey.covered_gfa_above,
    nextFromStack,
  );

  const areaIncreaseFromBelow = storeyArea - prevStoreyArea;
  const areaDecreaseToAbove = storeyArea - nextStoreyArea;

  if (areaIncreaseFromBelow > 0) {
    if (elevation <= 0) {
      gfa_ground_floor_slabs = areaIncreaseFromBelow;
    } else {
      gfa_cantilevered_floor_slabs = areaIncreaseFromBelow;
    }
  }
  if (areaDecreaseToAbove > 0) {
    // Roof at or above grade is sky. inner_height is shorter than the storey
    // so a basement lid at 0 was previously classified as a buried terrace.
    if (elevation + height < 0) {
      gfa_terrace_slabs = areaDecreaseToAbove;
    } else {
      gfa_sky = areaDecreaseToAbove;
    }
  }

  const { width: building_width } = estimateRectangleDimensions({
    area: storeyArea,
    perimeter: storeyPerimeter,
  });
  const gfa_activities = getActivitiesGFA(
    gfa_sky,
    gfa,
    building_width,
    inner_height,
  );

  const gfa_facades = CALCULATIONS.gfa_facades.calculate({
    perimeter: storeyPerimeter,
  });
  const gfa_installations = CALCULATIONS.gfa_installations.calculate({ gfa });

  // Design variables don't expose a legacy mezzanine edge length. We keep it 0 (calculated, but not invented).
  const mezzanine_edge = 0;
  const gwa_mezzanine = CALCULATIONS.gwa_mezzanine.calculate({
    mezzanine_edge,
    total_height,
  });

  const activityDerived = getActivityDerivedStoreyValues({
    activity: designVariables.programs,
    activityId,
    floorIndex,
    gfa,
    sumGfa: sumGFA,
    share,
    gfaActivities: gfa_activities,
    gfaFacades: gfa_facades,
    gfaInstallations: gfa_installations,
    innerHeight: inner_height,
    storeyPerimeter,
    isGroundFloor: storey.is_ground_floor,
  });

  return {
    ...emptyStoreySummedValues,
    name,

    // Base storey values
    gfa,
    facade_edge: storeyPerimeter,
    mezzanine_edge,
    inner_height,

    // Calculated storey values
    gfa_above_ground,
    gfa_below_ground,
    total_height,
    total_height_above_ground,
    total_height_below_ground,
    external_gwa,
    external_gwa_above_ground,
    external_gwa_below_ground,
    gross_volume,
    gross_volume_above_ground,
    gross_volume_below_ground,
    elevation,
    gfa_cantilevered_floor_slabs,
    gfa_terrace_slabs,
    gfa_sky,
    gfa_activities,
    gfa_facades,
    gfa_installations,
    ...activityDerived,
    gfa_ground_floor_slabs,
    perimeter: storeyPerimeter,
    gwa_mezzanine,
  };
};

const OTHER_ACTIVITIES_FOR_LEGACY_DISTRIBUTION: readonly ActivityType[] = [
  ActivityType.Other,
  ActivityType.Storage,
  ActivityType.Utilities,
] as const;

const getOtherActivitiesTotalGfa = (
  activity: DesignExpressionVariables['programs'],
): number => {
  return OTHER_ACTIVITIES_FOR_LEGACY_DISTRIBUTION.reduce((acc, type) => {
    return acc + (activity[type]?.gfa ?? 0);
  }, 0);
};

const getResidentialApartmentInputs = (
  activity: DesignExpressionVariables['programs'],
): {
  apartments_living_area_per_apartment: number;
  apartments_balcony_area_per_apartment: number;
} => {
  const residential = activity[ActivityType.Residential];
  if (!residential) {
    return {
      apartments_living_area_per_apartment: 0,
      apartments_balcony_area_per_apartment: 0,
    };
  }

  return {
    apartments_living_area_per_apartment:
      residential.apartments_living_area_per_apartment,
    apartments_balcony_area_per_apartment:
      residential.apartments_balcony_area_per_apartment,
  };
};

export type SumStoreysFromDesignExpressionVariablesResult = {
  storeys: IEnrichedStorey[];
  sum: IStoreySum;
};

/**
 * Sum floors of one stack: add areas and heights, keep the largest perimeter.
 */
const sumStoreySumsWithinBuilding = (storeys: IStoreySum[]): IStoreySum => {
  if (storeys.length === 0) return { ...emptyStoreySummedValues };
  const sum: IStoreySum = {
    ...emptyStoreySummedValues,
    elevation: storeys[0]?.elevation ?? 0,
  };
  const sumKeys = getKeys(omit(sum, 'name', 'elevation'));
  storeys.forEach((storey) => {
    sumKeys.forEach((key) => {
      const value = storey[key];
      if (!guards.is.number(value)) return;
      sum[key] += value;
    });
    sum.elevation = Math.min(sum.elevation, storey.elevation);
  });
  sum.perimeter = Math.max(0, ...storeys.map((s) => s.perimeter));
  sum.name = storeys.map((s, idx) => s.name || `Storey ${idx}`).join(', ');
  return sum;
};

/**
 * Drop blank {@link StoreySelector} values (`undefined`, `''`) so they do not
 * look like an empty filter to {@link selectByIndexesOrNames}.
 * Empty result means “all storeys”, same as calling {@link GetStoreyFn} with no args.
 */
export const resolveStoreySelectors = (
  selectors: StoreySelector[],
): (number | string)[] =>
  selectors.filter(
    (selector): selector is number | string =>
      guards.is.number(selector) ||
      (guards.is.string(selector) && !guards.is.emptyString(selector)),
  );

/**
 * Resolve {@link GetStoreyFn} selectors to indexes in the full storey list.
 * Per-storey calculations must keep the original floor index so ground-floor
 * formulas (e.g. {@link CALCULATIONS.gfa_floor_slabs}) stay correct when a
 * non-ground storey is selected alone.
 */
const getSelectedStoreyFloorIndexes = (
  storeys: StoreyExpressionVariables[],
  selectors: StoreySelector[],
): number[] => {
  const resolvedSelectors = resolveStoreySelectors(selectors);
  if (resolvedSelectors.length === 0) {
    return storeys.map((_, index) => index);
  }

  return selectByIndexesOrNames(storeys, resolvedSelectors)
    .map((storey) => storeys.indexOf(storey))
    .filter((index) => index >= 0);
};

/**
 * @deprecated (need expression migration). Sum all storeys from {@link DesignExpressionVariables} using the legacy `sumStoreys` output surface.
 * This is intended as a compatibility bridge while migrating from ProjectMetadata to BuildingDesign.
 * Henrik would keep the output contract stable so expression consumers don't need to change.
 *
 * No selectors sums every floor of every building. Numeric selectors pick that
 * floor index from each building and combine the results (sum areas/GWA, max
 * stack height), matching `getStorey`.
 */
export const sumStoreysFromDesignExpressionVariables = (
  designVariables: Pick<
    DesignExpressionVariables,
    'storeys' | 'programs' | 'storeysByBuilding'
  >,
  options: SumStoreysFromDesignOptions = {},
): SumStoreysFromDesignExpressionVariablesResult => {
  const selectors = options.selectors ?? [];
  const activityId = options.activityId ?? ActivityId.PrivateHousing;
  const storeysByBuilding = designVariables.storeysByBuilding;
  if (storeysByBuilding && storeysByBuilding.length > 1) {
    const siteGfa =
      options.siteGfa ??
      storeysByBuilding.reduce((acc, stack) => acc + getStackGfa(stack), 0);
    const buildingResults = storeysByBuilding
      .map((buildingStoreys) =>
        sumStoreysFromDesignExpressionVariables(
          {
            ...designVariables,
            storeys: buildingStoreys,
            storeysByBuilding: undefined,
          },
          { activityId, selectors, siteGfa },
        ),
      )
      .filter((result) => result.storeys.length > 0);
    if (buildingResults.length === 0) {
      return { storeys: [], sum: { ...emptyStoreySummedValues } };
    }
    return {
      storeys: buildingResults.flatMap((result) => result.storeys),
      sum: combineBuildingStoreySums(
        buildingResults.map((result) => result.sum),
      ),
    };
  }

  if (!designVariables || designVariables.storeys.length === 0) {
    return { storeys: [], sum: { ...emptyStoreySummedValues } };
  }

  // Compute the full stack first so sky / ground / cantilever see neighbours
  // even when the caller asked for a single floor via selectors.
  const storeySums: IStoreySum[] = designVariables.storeys.map(
    (_, floorIndex) =>
      calculateStoreySumFromDesignStoreyVariables(designVariables, floorIndex, {
        activityId,
        name: `Storey ${floorIndex}`,
        siteGfa: options.siteGfa,
      }),
  );

  const selectedIndexes = getSelectedStoreyFloorIndexes(
    designVariables.storeys,
    selectors,
  );

  if (selectedIndexes.length === 0) {
    return { storeys: [], sum: { ...emptyStoreySummedValues } };
  }

  const selectedStoreySums = selectedIndexes.flatMap((index) => {
    const sum = storeySums[index];
    return sum ? [sum] : [];
  });

  const stackGfa = storeySums.reduce((acc, s) => acc + s.gfa, 0);
  const shareDenominatorGfa = options.siteGfa ?? stackGfa;
  const activity = designVariables.programs;

  const stairwellsApartmentsPerStairwellPerStorey =
    activity[ActivityType.Stairwells]?.apartments_per_stairwell_per_storey ?? 0;
  const {
    apartments_living_area_per_apartment,
    apartments_balcony_area_per_apartment,
  } = getResidentialApartmentInputs(activity);

  // These are the same for every storey (legacy semantics), so compute once.
  const gfa_openings_per_stairwell_per_storey =
    CALCULATIONS.gfa_openings_per_stairwell_per_storey.calculate();
  const gfa_elevator_per_stairwell_per_storey =
    CALCULATIONS.gfa_elevator_per_stairwell_per_storey.calculate({
      elevators_per_stairwell:
        activity[ActivityType.Stairwells]?.elevators_per_stairwell ?? 0,
    });
  const gfa_floor_per_stairwell_per_storey =
    CALCULATIONS.gfa_floor_per_stairwell_per_storey.calculate({
      stairwells_apartments_per_stairwell_per_storey:
        stairwellsApartmentsPerStairwellPerStorey,
    });
  const gfa_per_stairwell_per_storey =
    CALCULATIONS.gfa_per_stairwell_per_storey.calculate({
      gfa_openings_per_stairwell_per_storey,
      gfa_elevator_per_stairwell_per_storey,
      gfa_floor_per_stairwell_per_storey,
    });
  const gfa_stairwell_per_living_area =
    CALCULATIONS.gfa_stairwell_per_living_area.calculate({
      stairwells_apartments_per_stairwell_per_storey:
        stairwellsApartmentsPerStairwellPerStorey,
      apartments_living_area_per_apartment,
      gfa_per_stairwell_per_storey,
    });
  const gfa_stairs_per_gfa_stairwell =
    CALCULATIONS.gfa_stairs_per_gfa_stairwell.calculate({
      gfa_per_stairwell_per_storey,
    });
  const gfa_floor_per_gfa_stairwell =
    CALCULATIONS.gfa_floor_per_gfa_stairwell.calculate({
      gfa_floor_per_stairwell_per_storey,
      gfa_per_stairwell_per_storey,
    });
  const gfa_stairwell_per_gfa_apartment =
    CALCULATIONS.gfa_stairwell_per_gfa_apartment.calculate({
      gfa_stairwell_per_living_area,
    });

  const resultsStoreys: IEnrichedStorey[] = selectedIndexes.flatMap((i) => {
    const storeySum = storeySums[i];
    if (!storeySum) return [];
    const share =
      shareDenominatorGfa > 0 ? storeySum.gfa / shareDenominatorGfa : 0;

    // Distribute building-level areas by storey GFA share (same approximation as legacy).
    const garage_gfa = (activity[ActivityType.Garage]?.gfa ?? 0) * share;
    const gfa_other_activities = getOtherActivitiesTotalGfa(activity) * share;

    const gfa_apartment_plus_stairwell =
      CALCULATIONS.gfa_apartment_plus_stairwell.calculate({
        gfa_activities: storeySum.gfa_activities,
        gfa_facades: storeySum.gfa_facades,
        gfa_installations: storeySum.gfa_installations,
        garage_gfa,
        gfa_other_activities,
      });

    const facade_pillar_count = CALCULATIONS.facade_pillar_count.calculate({
      perimeter: storeySum.perimeter,
      apartment_count: storeySum.apartment_count,
    });

    const { name: _name, ...storeySumWithoutName } = storeySum;

    return [
      {
        ...storeySumWithoutName,
        is_ground_floor:
          designVariables.storeys[i]?.is_ground_floor ?? Number(i === 0),
        stairwells_apartments_per_stairwell_per_storey:
          stairwellsApartmentsPerStairwellPerStorey,
        apartments_living_area_per_apartment,
        apartments_balcony_area_per_apartment,
        garage_gfa,
        gfa_other_activities,
        gfa_apartment_plus_stairwell,
        gfa_stairwell_per_gfa_apartment,
        gfa_openings_per_stairwell_per_storey,
        gfa_elevator_per_stairwell_per_storey,
        gfa_floor_per_stairwell_per_storey,
        gfa_per_stairwell_per_storey,
        gfa_stairwell_per_living_area,
        gfa_stairs_per_gfa_stairwell,
        gfa_floor_per_gfa_stairwell,
        facade_pillar_count,
      },
    ];
  });

  return {
    storeys: resultsStoreys,
    sum: sumStoreySumsWithinBuilding(selectedStoreySums),
  };
};

/**
 * @deprecated Migrate "activity" in expressions to use {@link ActivityType} instead.
 */
export const getActivityIdFromActivityType = (
  activityType: ActivityType,
): ActivityId => {
  switch (activityType) {
    case ActivityType.Residential:
      return ActivityId.Residential;
    case ActivityType.Office:
      return ActivityId.Office;
    case ActivityType.School:
      return ActivityId.School;
    case ActivityType.Industry:
      return ActivityId.Industry;
    case ActivityType.Garage:
      return ActivityId.ResidentParking;
  }
  return ActivityId.Residential;
};

/**
 * Pick {@link StoreySummaryVariables} from a storey sum.
 * Keys match {@link emptyStoreySummaryVariables} so runtime and the obsolete-variable audit stay aligned.
 */
export const toGetStoreySummary = (
  storeySum: IStoreySum,
): StoreySummaryVariables => {
  const summary: StoreySummaryVariables = { ...emptyStoreySummaryVariables };
  const record: Record<string, unknown> = { ...storeySum };
  getKeys(emptyStoreySummaryVariables).forEach((key) => {
    const value = record[key];
    if (guards.is.number(value)) {
      summary[key] = value;
    }
  });
  return summary;
};

/**
 * Largest design program as {@link ActivityType} plus the legacy {@link ActivityId} mapping.
 */
export const getLargestDesignActivity = (
  designVariables: Pick<DesignExpressionVariables, 'programs'>,
): { activityType: ActivityType; activityId: ActivityId } => {
  const activityType = getLargestProgramActivityTypeFromActivityMix(
    activityExpressionVariablesRecordToActivityMix(designVariables.programs),
  );
  return {
    activityType,
    activityId: getActivityIdFromActivityType(activityType),
  };
};

/**
 * Legacy {@link ActivityId} from the largest design program.
 */
export const getActivityIdFromDesignExpressionVariables = (
  designVariables: Pick<DesignExpressionVariables, 'programs'>,
): ActivityId => getLargestDesignActivity(designVariables).activityId;

type GetStoreySumFromDesignOptions = {
  /**
   * Site GFA used as the activity-share denominator. Pass the sum of every
   * building when summing one stack of a multi-building site so garage and
   * other program areas are allocated once, not once per building.
   */
  siteGfa?: number;
};

/**
 * {@link IStoreySum} from design storeys and programs (largest program as activity).
 * Always recalculates; does not read unused {@link DesignExpressionVariables.storeySum}.
 * Pass `siteGfa` as the site total when this is called per building and the
 * results are combined, so garage and similar program areas are not counted
 * once per stack.
 */
export const getStoreySumFromDesignExpressionVariables = (
  designVariables: Pick<DesignExpressionVariables, 'storeys' | 'programs'>,
  selectors: StoreySelector[] = [],
  options: GetStoreySumFromDesignOptions = {},
): IStoreySum =>
  sumStoreysFromDesignExpressionVariables(designVariables, {
    activityId: getActivityIdFromDesignExpressionVariables(designVariables),
    selectors,
    siteGfa: options.siteGfa,
  }).sum;
